import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Ban, KeyRound, RefreshCw, ScrollText, ShieldCheck, Siren, UserRound } from "lucide-react";
import AdminShell from "../shell/AdminShell";
import { NAV_INDEX } from "../shell/AdminNav";
import DataTable from "../components/DataTable";
import StatCard, { StatGrid } from "../components/StatCard";
import ConfirmDialog from "../components/ConfirmDialog";
import { ErrorState, Loading, NotApplicable, NotFound, PermissionDenied } from "../components/States";
import { AdminProvider, useAdmin } from "../lib/useAdmin";
import { get, post, adminError, ERR } from "../lib/adminApi";
import { LIVE, NOT_APPLICABLE, TITLES } from "./registry";
import "../admin.css";

const ROOT = "/admin";
const keyFromPath = path => {
  const rest = path.replace(/^\/admin\/?/, "");
  if (!rest) return "dashboard";
  const entry = Object.values(NAV_INDEX || {}).find(i => i.to === path || i.to === `/${rest}`);
  return entry?.key || rest.split("/")[0];
};

/* ---------------------------------------------------------- données réelles */

function useEndpoint(path, enabled = true) {
  const [state, setState] = useState({ data: null, loading: enabled, error: null });
  const load = useCallback(() => {
    if (!enabled) return;
    setState(s => ({ ...s, loading: true, error: null }));
    get(path)
      .then(data => setState({ data, loading: false, error: null }))
      .catch(error => setState({ data: null, loading: false, error }));
  }, [path, enabled]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

function Dashboard() {
  const { data, loading, error, reload } = useEndpoint("/overview");
  if (loading) return <Loading label="Lecture des compteurs…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  const u = data.users || {}, s = data.sessions || {}, a = data.audit || {};
  return (
    <>
      <StatGrid>
        <StatCard label="Comptes" value={u.total} hint={`${u.new_7d ?? 0} sur 7 jours`} icon={UserRound} />
        <StatCard label="Sessions actives" value={s.active} hint={`${s.impersonations_active ?? 0} emprunt(s)`} icon={KeyRound} />
        <StatCard label="Liens en attente" value={data.login_links?.pending} hint="expirent en 15 min" icon={RefreshCw} />
        <StatCard label="Audit 24 h" value={a.events_24h} hint={`${a.denied_24h ?? 0} refus`} icon={ScrollText} />
        <StatCard label="Suspendus" value={u.suspended} icon={Ban} />
        <StatCard label="Administrateurs" value={u.admins} icon={ShieldCheck} />
      </StatGrid>
      <NotApplicable
        title="Revenus, croissance et IA : non mesurés"
        description="Ces indicateurs n'ont aucune donnée en base. Nous les affichons comme absents plutôt qu'à zéro, ce qui laisserait croire à une mesure."
        details={(data.unavailable || []).map(x => (typeof x === "string" ? x : x.label || x.key))}
      />
    </>
  );
}

const USER_COLUMNS = [
  { key: "email", header: "Adresse", sortable: true, minWidth: 220, locked: true },
  { key: "username", header: "Pseudo", sortable: true, render: r => r.username || <span className="adm-muted">—</span> },
  { key: "role", header: "Rôle", sortable: true },
  { key: "status", header: "Statut", sortable: true, render: r => r.status === "suspended"
      ? <span className="adm-tag adm-tag-danger">Suspendu</span>
      : <span className="adm-tag adm-tag-ok">Actif</span> },
  { key: "admin_roles", header: "Rôles admin", render: r => (r.admin_roles || []).join(", ") || <span className="adm-muted">—</span> },
  { key: "created_at", header: "Créé le", sortable: true, render: r => new Date(r.created_at).toLocaleDateString("fr-FR") },
];

function Users() {
  const { can } = useAdmin();
  const { data, loading, error, reload } = useEndpoint("/users?limit=100", can("users.read"));
  const [pending, setPending] = useState(null);

  if (!can("users.read")) return <PermissionDenied permission="users.read" requiredRole="Administrateur avec lecture des comptes" />;

  const act = async (action, ids) => {
    await post("/users/bulk", { action, user_ids: ids });
    reload();
  };

  return (
    <>
      <DataTable
        id="admin-users"
        columns={USER_COLUMNS}
        rows={data?.items || data?.users || []}
        getRowId={r => r.id}
        loading={loading}
        error={error}
        onRetry={reload}
        searchable
        exportName="utilisateurs"
        selectable={can("users.write")}
        bulkActions={can("users.write") ? [
          { id: "suspend", label: "Suspendre", tone: "danger", run: ids => setPending({ action: "suspend", ids }) },
          { id: "reactivate", label: "Réactiver", run: ids => act("reactivate", ids) },
          { id: "revoke_sessions", label: "Révoquer les sessions", run: ids => act("revoke_sessions", ids) },
        ] : []}
        emptyTitle="Aucun compte"
        emptyDescription="La plateforme n'a encore aucun utilisateur inscrit."
      />
      <ConfirmDialog
        open={!!pending}
        onClose={() => setPending(null)}
        title="Suspendre ces comptes ?"
        description={`${pending?.ids.length ?? 0} compte(s) seront suspendus.`}
        consequences={[
          "Toutes leurs sessions en cours sont révoquées immédiatement.",
          "Ils ne pourront plus ouvrir de nouvelle session, même avec un lien valide.",
          "L'action est journalisée avec votre identité et votre motif.",
        ]}
        confirmLabel="Suspendre"
        level="reauth"
        onConfirm={() => act("suspend", pending.ids)}
      />
    </>
  );
}

const AUDIT_COLUMNS = [
  { key: "at", header: "Horodatage", sortable: true, minWidth: 170, locked: true,
    render: r => new Date(r.at).toLocaleString("fr-FR") },
  { key: "actor_email", header: "Auteur", sortable: true, render: r => r.actor_email || <span className="adm-muted">anonyme</span> },
  { key: "action", header: "Action", sortable: true, minWidth: 200 },
  { key: "resource_type", header: "Ressource", render: r => r.resource_type || <span className="adm-muted">—</span> },
  { key: "result", header: "Résultat", sortable: true, render: r => r.result === "denied"
      ? <span className="adm-tag adm-tag-danger">Refusé</span>
      : <span className="adm-tag adm-tag-ok">Succès</span> },
  { key: "ip", header: "IP", render: r => r.ip || <span className="adm-muted">—</span> },
];

function Audit() {
  const { can } = useAdmin();
  const { data, loading, error, reload } = useEndpoint("/audit?limit=100", can("audit.read"));
  if (!can("audit.read")) return <PermissionDenied permission="audit.read" requiredRole="Administrateur avec lecture de l'audit" />;
  return (
    <DataTable
      id="admin-audit" columns={AUDIT_COLUMNS} rows={data?.items || data?.events || []}
      getRowId={r => r.id || r._id} loading={loading} error={error} onRetry={reload}
      searchable exportName="journal-audit"
      emptyTitle="Aucun événement" emptyDescription="Le journal est vide : aucune action sensible n'a encore été effectuée."
    />
  );
}

const GENERIC = {
  sessions: { path: "/sessions", perm: "security.read", name: "sessions", columns: [
    { key: "user_email", header: "Compte", minWidth: 220, locked: true, render: r => r.user_email || r.user_id },
    { key: "created_at", header: "Ouverte le", sortable: true, render: r => new Date(r.created_at).toLocaleString("fr-FR") },
    { key: "expires_at", header: "Expire le", sortable: true, render: r => new Date(r.expires_at).toLocaleString("fr-FR") },
    { key: "ua", header: "Navigateur", render: r => (r.ua || "—").slice(0, 60) },
    { key: "impersonated_by", header: "Emprunt", render: r => r.impersonated_by ? <span className="adm-tag adm-tag-warn">Oui</span> : <span className="adm-muted">—</span> },
  ]},
  flags: { path: "/flags", perm: "feature_flags.read", name: "feature-flags", columns: [
    { key: "key", header: "Clé", sortable: true, minWidth: 200, locked: true },
    { key: "description", header: "Description", minWidth: 260 },
    { key: "enabled", header: "Actif", sortable: true, render: r => r.enabled
        ? <span className="adm-tag adm-tag-ok">Oui</span> : <span className="adm-tag">Non</span> },
    { key: "rollout_percent", header: "Déploiement", render: r => `${r.rollout_percent ?? 0} %` },
    { key: "environment", header: "Environnement" },
  ]},
  incidents: { path: "/incidents", perm: "incidents.read", name: "incidents", columns: [
    { key: "title", header: "Titre", sortable: true, minWidth: 240, locked: true },
    { key: "severity", header: "Gravité", sortable: true, render: r => <span className="adm-tag adm-tag-danger">{(r.severity || "").toUpperCase()}</span> },
    { key: "status", header: "Statut", sortable: true },
    { key: "started_at", header: "Début", sortable: true, render: r => r.started_at ? new Date(r.started_at).toLocaleString("fr-FR") : "—" },
  ]},
  exports: { path: "/exports", perm: "data.export", name: "exports", columns: [
    { key: "type", header: "Type", sortable: true, locked: true },
    { key: "format", header: "Format" },
    { key: "status", header: "Statut", sortable: true },
    { key: "requested_at", header: "Demandé le", sortable: true, render: r => r.requested_at ? new Date(r.requested_at).toLocaleString("fr-FR") : "—" },
  ]},
  gdpr: { path: "/privacy-requests", perm: "data.export", name: "demandes-rgpd", columns: [
    { key: "type", header: "Type", sortable: true, locked: true },
    { key: "subject", header: "Personne concernée", minWidth: 220 },
    { key: "status", header: "Statut", sortable: true },
    { key: "due_at", header: "Échéance", sortable: true, render: r => r.due_at ? new Date(r.due_at).toLocaleDateString("fr-FR") : "—" },
  ]},
};

function GenericTable({ section }) {
  const cfg = GENERIC[section];
  const { can } = useAdmin();
  const allowed = can(cfg.perm);
  const { data, loading, error, reload } = useEndpoint(cfg.path, allowed);
  if (!allowed) return <PermissionDenied permission={cfg.perm} />;
  const rows = data?.items || data?.[cfg.name] || data?.[section] || (Array.isArray(data) ? data : []);
  return (
    <DataTable
      id={`admin-${section}`} columns={cfg.columns} rows={rows}
      getRowId={(r, i) => r.id || r._id || r.key || i}
      loading={loading} error={error} onRetry={reload}
      searchable exportName={cfg.name}
      emptyTitle="Rien à afficher" emptyDescription="Aucune entrée pour le moment."
    />
  );
}

function Security() {
  const { can } = useAdmin();
  const { data, loading, error, reload } = useEndpoint("/security/overview", can("security.read"));
  if (!can("security.read")) return <PermissionDenied permission="security.read" />;
  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  return (
    <>
      <StatGrid>
        {Object.entries(data.counters || data.metrics || {}).map(([k, v]) => (
          <StatCard key={k} label={k.replace(/_/g, " ")} value={typeof v === "number" ? v : String(v)} icon={ShieldCheck} />
        ))}
      </StatGrid>
      <NotApplicable
        title="Ce que nous ne mesurons pas encore"
        description="Ces signaux exigeraient une instrumentation que nous n'avons pas mise en place. Les afficher à zéro donnerait une fausse impression de surveillance."
        details={(data.unavailable || []).map(x => (typeof x === "string" ? x : x.label || x.key || x.reason))}
      />
    </>
  );
}

function Roles() {
  const { can } = useAdmin();
  const { data, loading, error, reload } = useEndpoint("/me", true);
  if (!can("settings.read")) return <PermissionDenied permission="settings.read" />;
  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  return (
    <DataTable
      id="admin-roles"
      columns={[
        { key: "permission", header: "Permission accordée", locked: true, minWidth: 260 },
      ]}
      rows={(data.permissions || []).map(p => ({ id: p, permission: p }))}
      getRowId={r => r.id}
      searchable exportName="mes-permissions"
      emptyTitle="Aucune permission" emptyDescription="Votre compte ne porte aucun rôle d'administration."
      toolbarExtra={<span className="adm-muted">Rôles : {(data.roles || []).join(", ") || "aucun"}</span>}
    />
  );
}

/* ------------------------------------------------------------------- routeur */

function Section({ section }) {
  if (section === "dashboard") return <Dashboard />;
  if (section === "users") return <Users />;
  if (section === "audit") return <Audit />;
  if (section === "security") return <Security />;
  if (section === "roles" || section === "permissions") return <Roles />;
  if (GENERIC[section]) return <GenericTable section={section} />;
  if (section === "impersonation") {
    return <NotApplicable
      title="L'emprunt démarre depuis une fiche utilisateur"
      description="Ouvrez un compte dans la section Utilisateurs pour lancer un emprunt. Un motif d'au moins dix caractères est obligatoire, la session dure trente minutes au maximum, et tout est journalisé." />;
  }
  const reason = NOT_APPLICABLE[section];
  if (reason) return <NotApplicable title="Sans objet aujourd'hui" description={reason} />;
  return <NotFound title="Section inconnue" description="Cette page n'existe pas dans la console." />;
}

function ConsoleInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const section = keyFromPath(location.pathname);
  const [title, subtitle] = TITLES[section] || [
    NAV_INDEX?.[section]?.label || "Console", LIVE.has(section) ? "" : "Section sans données à ce jour.",
  ];

  const searchSources = useMemo(() => [
    { id: "users", label: "Utilisateurs", icon: UserRound, perm: "users.read",
      search: async q => {
        try {
          const r = await get(`/users?q=${encodeURIComponent(q)}&limit=6`);
          return (r.items || r.users || []).map(u => ({ id: u.id, label: u.email, hint: u.username || u.role, run: () => navigate("/admin/utilisateurs") }));
        } catch { return []; }
      } },
  ], [navigate]);

  const commandActions = useMemo(() => [
    { id: "go-audit", label: "Ouvrir le journal d'audit", group: "Navigation", icon: ScrollText, perm: "audit.read", run: () => navigate("/admin/audit") },
    { id: "go-users", label: "Ouvrir les utilisateurs", group: "Navigation", icon: UserRound, perm: "users.read", run: () => navigate("/admin/utilisateurs") },
    { id: "go-incidents", label: "Ouvrir les incidents", group: "Navigation", icon: Siren, perm: "incidents.read", run: () => navigate("/admin/incidents") },
  ], [navigate]);

  return (
    <AdminShell
      title={title}
      subtitle={subtitle}
      currentPath={location.pathname}
      onNavigate={to => navigate(to)}
      searchSources={searchSources}
      commandActions={commandActions}
      env={process.env.REACT_APP_ENV || (window.location.hostname === "performcustom.com" ? "production" : "development")}
    >
      <Section section={section} />
    </AdminShell>
  );
}

export default function AdminConsole() {
  return (
    <AdminProvider>
      <ConsoleInner />
    </AdminProvider>
  );
}
